<template>
  <div style="height:100%" ref="wrapper">
    <q-resize-observer @resize="onResize" :debounce="350" />
    <GmapMap
      :center="currentMapPosition"
      :zoom="zoom"
      ref="map"
      map-type-id="terrain"
      :style="mapStyles"
      :options="{
        fullscreenControl: false,
        gestureHandling: 'greedy',
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }],
          },
        ],
        minZoom: 3,
      }"
      @zoom_changed="handleZoomChanged"
      @bounds_changed="handleBoundsChanged"
    >
      <template #visible>
        <q-card class="shadow-1 gt-xs" id="legend">
          <div>
            <img :src="pin_a52714_half" />
            Private
          </div>
          <div>
            <img :src="pin_ffd600_half" />
            Semi-Private
          </div>
          <div>
            <img :src="pin_097138_half" />
            Public
          </div>
          <div>
            <img :src="pin_01579b_half" />
            Resort
          </div>
          <div>
            <img :src="pin_9c27b0_half" />
            Muni
          </div>
        </q-card>
        <q-btn
          class="filter-status shadow-1"
          :color="
            filterInput.length || weatherFilterInput || recentlyAdded
              ? 'yellow-11'
              : 'white'
          "
          text-color="black"
          @click="toggleFilters"
          :ripple="true"
          no-caps
          dense
          size="md"
          v-show="courses.length > 0"
        >
          <div class="row items-center no-wrap">
            <q-icon
              v-if="filterInput.length || weatherFilterInput"
              color="grey-6"
              name="filter_list"
              style="padding-right: 6px"
            />
            <div class="text-center">
              <span>
                <span>{{ filteredCoursesCountFormatted }}</span>
                <span> of {{ filteredCoursesTotalCountFormatted }}</span></span
              >
              <span
                v-show="
                  filteredCoursesTotalCountFormatted !== coursesCountFormatted
                "
                ><br /><span class="text-caption text-grey-8"
                  >{{ coursesCountFormatted }} Total</span
                ></span
              >
            </div>
          </div>
        </q-btn>
        <q-btn
          class="reset-filters"
          v-show="filterInput.length || weatherFilterInput || recentlyAdded"
          color="red-2"
          dense
          text-color="black"
          size="sm"
          push
          @click="clickResetFilters"
          >Clear All Filters</q-btn
        >
        <q-card class="gt-xs geolocation shadow-1">
          <q-btn
            size="md"
            dense
            @click="centerGeolocation"
            icon="my_location"
            flat
            :ripple="true"
          />
        </q-card>
        <q-card class="lt-sm geolocation geolocation--mobile shadow-1">
          <q-btn
            size="md"
            dense
            @click="centerGeolocation"
            icon="my_location"
            flat
            :ripple="true"
          />
        </q-card>
        <q-btn
          class="action-button shadow-3 text-weight-bold"
          :class="{ 'action-button--premium': isPremium }"
          text-color="black"
          color="white"
          @click="toggleFilters"
          :ripple="false"
          push
          size="md"
        >
          <div class="row items-center no-wrap">
            <q-icon left name="filter_alt" />
            <div class="text-center">Course<br />Filters</div>
          </div>
        </q-btn>
      </template>
      <GmapInfoWindow
        :position="mini_course_view_window_position"
        :opened="mini_course_view_window_open"
        :options="mini_course_view_window_options"
        @closeclick="handleMiniCourseViewClose"
      >
        <MiniCourseView
          :datapoints="miniCourseViewDatapoints"
          :course="mini_course_view_window_options.course"
          :wishlist-course-ids="wishlistCourseIds"
          :course-played-course-ids="coursePlayedCourseIds"
        />
      </GmapInfoWindow>
      <GmapMarker
        @mouseover="handleMarkerMouseover(marker.course.id)"
        @mouseout="handleMarkerMouseout"
        @click="handleMarkerClick(marker.course.id)"
        :key="marker.course.id"
        v-for="marker in filteredMarkers"
        :position="marker.latLng"
        :icon="marker.icon"
        :clickable="true"
      >
      </GmapMarker>
    </GmapMap>
    <q-dialog
      v-model="course_view_window_open"
      @before-hide="resetClickedCourseId"
    >
      <q-layout container class="bg-white">
        <q-header class="bg-primary">
          <q-toolbar>
            <q-toolbar-title>Course Details</q-toolbar-title>
            <q-btn
              flat
              @click="handleCourseViewClose"
              round
              dense
              icon="close"
            />
          </q-toolbar>
        </q-header>
        <q-page-container>
          <q-page>
            <CourseView
              :datapoints="courseViewDatapoints"
              :course="course_view_window_options.course"
              :wishlist-course-ids="wishlistCourseIds"
              @toggle-wishlist="toggleWishlist"
              :course-played-course-ids="coursePlayedCourseIds"
              @toggle-course-played="toggleCoursePlayed"
            />
          </q-page>
        </q-page-container>
      </q-layout>
    </q-dialog>
  </div>
</template>
<script>
import debounce from "lodash/debounce";
import { mapActions, mapGetters, mapState } from "vuex";
import CourseView from "@/components/CourseView.vue";
import MiniCourseView from "@/components/MiniCourseView.vue";
import pin_9c27b0 from "@/assets/1899-blank-shape_pin_4x-9c27b0.png";
import pin_01579b from "@/assets/1899-blank-shape_pin_4x-01579b.png";
import pin_097138 from "@/assets/1899-blank-shape_pin_4x-097138.png";
import pin_a52714 from "@/assets/1899-blank-shape_pin_4x-a52714.png";
import pin_ffd600 from "@/assets/1899-blank-shape_pin_4x-ffd600.png";
import pin_9c27b0_half from "@/assets/1899-blank-shape_pin_4x-9c27b0-half.png";
import pin_01579b_half from "@/assets/1899-blank-shape_pin_4x-01579b-half.png";
import pin_097138_half from "@/assets/1899-blank-shape_pin_4x-097138-half.png";
import pin_a52714_half from "@/assets/1899-blank-shape_pin_4x-a52714-half.png";
import pin_ffd600_half from "@/assets/1899-blank-shape_pin_4x-ffd600-half.png";
import COUNTRIES from "@/data/countries.json";
import { DEFAULT_COUNTRY } from "@/constants";

const DEFAULT_ZOOM = 7;
const CURRENT_LOCATION_ZOOM = 10;
// the center of the U.S.
const DEFAULT_GEOLOCATION = { lat: 39.8097343, lng: -98.5556199 };

export default {
  name: "Map",
  components: {
    CourseView,
    MiniCourseView,
  },
  data() {
    return {
      zoom: DEFAULT_ZOOM,
      // legend_open: true,
      course_view_window_open: false,
      course_view_window_position: null,
      course_view_window_options: {
        // content: "",
        pixelOffset: {
          width: 0,
          height: -35,
        },
        course: {},
      },
      mini_course_view_window_open: false,
      mini_course_view_window_position: null,
      mini_course_view_window_options: {
        // content: "",
        pixelOffset: {
          width: 0,
          height: -35,
        },
        disableAutoPan: true,
        course: {},
      },
      currentMarkerIndex: null,
      report: "",
      mapsApiLoaded: false,
      markerMouseEnterTimer: null,
      markerMouseLeaveTimer: null,
      currentMapBounds: null,
      currentMapPosition: {
        lat: DEFAULT_GEOLOCATION.lat,
        lng: DEFAULT_GEOLOCATION.lng,
      },
      currentGeolocation: null,
      lastMapBounds: null,
    };
  },
  computed: {
    ...mapState([
      "courseMetadata",
      "hoveredCourseId",
      "clickedCourseId",
      "filterInput",
      "weatherFilterInput",
      "mapBounds",
      "smartZoom",
      "recentlyAdded",
      "userProfile",
      "mapVisited",
    ]),
    ...mapGetters([
      "isPremium",
      "courses",
      "courseViewDatapoints",
      "miniCourseViewDatapoints",
      "filteredCourses",
      "filteredCoursesAll",
      "wishlistCourseIds",
      "coursePlayedCourseIds",
      "coursesCountFormatted",
      "lastGeolocation",
      "preferredCountry",
    ]),
    filteredMarkers() {
      const courses = this.filteredCourses;

      return courses.map(m => {
        return {
          latLng: {
            lat: m.latitude,
            lng: m.longitude,
          },
          course: m,
          icon: this.getMarkerColor(m.OLGDP003),
        };
      });
    },
    filteredMarkersAll() {
      const courses = this.filteredCoursesAll;

      return courses.map(m => {
        return {
          latLng: {
            lat: m.latitude,
            lng: m.longitude,
          },
        };
      });
    },
    hoveredMarker() {
      return this.filteredMarkers.find(
        m => m.course.id === this.hoveredCourseId,
      );
    },
    clickedMarker() {
      return this.filteredMarkers.find(
        m => m.course.id === this.clickedCourseId,
      );
    },
    mapStyles() {
      const { height, width } = this.report;
      return {
        height: `${height}px`,
        width: `${width}px`,
      };
    },
    numberFormatter() {
      return new Intl.NumberFormat("en-US");
    },
    coursesCountFormatted() {
      return this.numberFormatter.format(this.courses.length);
    },
    filteredCoursesCountFormatted() {
      return this.numberFormatter.format(this.filteredCourses.length);
    },
    filteredCoursesTotalCountFormatted() {
      return this.numberFormatter.format(this.filteredCoursesAll.length);
    },
    preferredCountryComputed() {
      const preferredCountry = this.preferredCountry || DEFAULT_COUNTRY;
      const country = COUNTRIES.find(c => c.code === preferredCountry);

      if (!country || !country.lat || !country.lng) {
        // TODO: revisit returning null
        return null;
      }

      return country;
    },
  },
  beforeCreate() {
    this.pin_9c27b0_half = pin_9c27b0_half;
    this.pin_01579b_half = pin_01579b_half;
    this.pin_097138_half = pin_097138_half;
    this.pin_a52714_half = pin_a52714_half;
    this.pin_ffd600_half = pin_ffd600_half;
  },
  created() {
    let lastMapBoundsSessionExists = this.$session.exists("lastMapBounds");

    if (lastMapBoundsSessionExists) {
      // console.debug("lastMapBounds found in session storage, loading...");

      this.lastMapBounds = this.$session.get("lastMapBounds");
    }
  },
  mounted() {
    this.$gmapApiPromiseLazy().then(() => {
      console.debug("gmaps api loaded...");

      this.mapsApiLoaded = true;
    });
  },
  watch: {
    lastMapBounds(newVal, oldVal) {
      if (newVal !== oldVal) {
        // console.debug(
        //   "watch lastMapBounds, updating lastMapBounds in session storage...",
        // );

        this.$session.set("lastMapBounds", newVal);
      }
    },
    currentGeolocation(newVal) {
      if (!newVal || !this.$refs.map) {
        return;
      }

      console.debug(
        "watch currentGeolocation, panned to currentGeolocation and zoomed to the current location zoom...",
      );

      this.$refs.map.$mapObject.setCenter(newVal);
      this.handleZoomChanged(CURRENT_LOCATION_ZOOM);
    },
    mapsApiLoaded(newVal) {
      if (!newVal) {
        return;
      }

      this.$emit("maps-api-loaded");
    },
    filteredMarkers(markers) {
      if (!this.mapsApiLoaded) {
        return;
      }

      if (this.smartZoom) {
        return;
      }

      if (markers.length > 0) {
        const currentMapBounds = this.currentMapBounds;

        if (!currentMapBounds) {
          console.debug(
            "watch filteredMarkers, no currentMapBounds, bailing...",
          );

          return;
        }

        if (!this.mapVisited) {
          console.debug(
            "watch filteredMarkers, first time visitor, skipping bounds fit...",
          );

          return;
        }

        if (markers.length === this.courses.length) {
          console.debug("watch filteredMarkers, showing all courses.");

          // TODO: consider extracting center to preferredCountry code into method
          // then consider calling it from here when all courses are visible

          return;
        }

        const markersInBounds = markers.filter(m =>
          currentMapBounds.contains(m.latLng),
        );

        if (!markersInBounds.length) {
          const bounds = new window.google.maps.LatLngBounds();

          console.debug(
            "watch filteredMarkers, there are no markers in view, should fitBounds(bounds)",
          );

          for (let m of markers) {
            bounds.extend(m.latLng);
          }

          this.$refs.map.fitBounds(bounds);
        }

        // console.debug(
        //   "watch filteredMarkers, return visitor, applying marker bounds fit...",
        //   this.smartZoom,
        // );

        // this.$refs.map.fitBounds(bounds);
      }
    },
    hoveredCourseId() {
      if (!this.hoveredMarker) {
        this.mini_course_view_window_open = false;
        return;
      }
      this.showMiniCourseView(this.hoveredMarker);
    },
    clickedCourseId() {
      if (!this.clickedMarker) {
        this.course_view_window_open = false;
        return;
      }

      this.showCourseView(this.clickedMarker);
    },
    smartZoom(newVal, oldVal) {
      // console.debug("watch smartZoom", newVal, oldVal);

      if (newVal) {
        this.$store.commit("setMapBounds", this.currentMapBounds);
      }
    },
    lastGeolocation(newVal) {
      console.debug("watch lastGeolocation (from user profile)", newVal);

      if (!newVal) {
        console.debug("watch lastGeolocation, no value, bailing...", newVal);

        return;
      }
    },
    currentMapBounds(newVal, oldVal) {
      // console.debug("watch currentMapBounds", newVal, oldVal);
      // console.debug("watch currentMapBounds, mapVisited", this.mapVisited);
      // console.debug(
      //   "watch currentMapBounds, preferredCountryComputed",
      //   this.preferredCountryComputed,
      // );
      // console.debug(
      //   "watch currentMapBounds, lastGeolocation",
      //   this.lastGeolocation,
      // );

      if (!oldVal && !this.mapVisited) {
        console.debug(
          "watch currentMapBounds, welcome first time map visitor, we should center on 1. lastGeolocation, 2. preferred country",
        );

        const lastGeolocation = this.lastGeolocation;
        const { code, lat, lng } = this.preferredCountryComputed;

        if (lastGeolocation) {
          this.currentGeolocation = lastGeolocation;
        } else if (code && lat && lng) {
          const LatLng = new window.google.maps.LatLng(lat, lng);

          this.$refs.map.$mapObject.setCenter(LatLng);
          this.handleZoomChanged(DEFAULT_ZOOM);
        }

        this.$store.commit("setMapVisited", true);

        return;
      } else if (!oldVal && this.mapVisited && !this.smartZoom) {
        console.debug(
          "watch currentMapBounds, welcome return visitor, smart zoom is disabled, we should center on 1. lastMapBounds, 2. lastGeolocation, 3. preferredCountry",
        );

        const lastMapBounds = this.lastMapBounds;
        const lastGeolocation = this.lastGeolocation;
        const { code, lat, lng } = this.preferredCountryComputed;

        if (lastMapBounds) {
          this.$refs.map.fitBounds(this.lastMapBounds, 0);
        } else if (lastGeolocation) {
          this.currentGeolocation = lastGeolocation;
        } else if (code && lat && lng) {
          const LatLng = new window.google.maps.LatLng(lat, lng);

          this.$refs.map.$mapObject.setCenter(LatLng);
          this.handleZoomChanged(DEFAULT_ZOOM);
        }

        return;
      } else if (
        !oldVal &&
        this.mapVisited &&
        this.smartZoom &&
        this.mapBounds
      ) {
        console.debug(
          "watch currentMapBounds, welcome return visitor, smart zoom is enabled, we should center on mapBounds",
        );

        this.$refs.map.fitBounds(this.mapBounds, 0);

        return;
      }

      // console.debug("watch currentMapBounds, updating lastMapBounds");

      this.lastMapBounds = newVal;
    },
    filteredMarkersAll(val) {
      // console.debug("watch filteredMarkersAll", val, this.smartZoom);

      if (!this.smartZoom) {
        return;
      }

      if (val.length > 0 && this.filteredMarkers.length === 0) {
        console.debug(
          "watch filteredMarkersAll, smartZoom enabled, should fit to marker bounds...",
        );

        const markers = val;
        const bounds = new window.google.maps.LatLngBounds();

        for (let m of markers) {
          bounds.extend(m.latLng);
        }

        this.$refs.map.fitBounds(bounds);
      }
    },
  },
  methods: {
    ...mapActions(["updateWishlist", "updateCoursePlayed"]),
    centerGeolocation() {
      navigator.geolocation.getCurrentPosition(position => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;

        this.currentGeolocation = {
          lat,
          lng,
        };

        this.$nextTick(() => {
          this.setLastGeolocation(this.currentGeolocation);
        });
      });
    },
    getMarkerColor(type) {
      if (type === "Resort") {
        return pin_01579b;
      } else if (type === "Private") {
        return pin_a52714;
      } else if (type === "Public") {
        return pin_097138;
      } else if (type === "Semi-Private") {
        return pin_ffd600;
      } else if (type === "Muni") {
        return pin_9c27b0;
      }
    },
    handleMarkerMouseover(id) {
      clearTimeout(this.markerMouseLeaveTimer);

      this.markerMouseEnterTimer = setTimeout(() => {
        this.setHoveredCourseId(id);
        this.$emit("marker-hover", id);
      }, 650);
    },
    showMiniCourseView: function(marker) {
      this.mini_course_view_window_position = marker.latLng;
      this.mini_course_view_window_options.course = marker.course;
      //check if its the same marker that was selected if yes toggle
      if (this.currentMarkerId === marker.course.id) {
        this.mini_course_view_window_open = !this.mini_course_view_window_open;
      }
      //if different marker set infowindow to open and reset current marker index
      else {
        this.mini_course_view_window_open = true;
        this.currentMarkerId = marker.course.id;
      }
    },
    handleMarkerMouseout() {
      this.resetHoveredCourseId();
      this.mini_course_view_window_open = false;
      clearTimeout(this.markerMouseEnterTimer);

      this.markerMouseLeaveTimer = setTimeout(() => {
        this.resetHoveredCourseId();
        this.mini_course_view_window_open = false;
      }, 650);
    },
    handleMiniCourseViewClose() {
      this.mini_course_view_window_open = false;
    },
    handleMarkerClick(id) {
      this.setClickedCourseId(id);
    },
    showCourseView: function(marker) {
      this.course_view_window_position = marker.latLng;
      this.course_view_window_options.course = marker.course;
      this.mini_course_view_window_open = false;
      this.course_view_window_open = false;
      //check if its the same marker that was selected if yes toggle
      if (this.currentMarkerId === marker.course.id) {
        this.course_view_window_open = !this.course_view_window_open;
      }
      //if different marker set infowindow to open and reset current marker index
      else {
        this.course_view_window_open = true;
        this.currentMarkerId = marker.course.id;
      }
    },
    handleCourseViewClose() {
      const courseId = this.clickedCourseId;

      this.setHoveredCourseId(courseId);
      this.resetClickedCourseId();
      this.course_view_window_open = false;
    },
    toggleWishlist(id) {
      this.$store.dispatch("updateWishlist", id);
    },
    setHoveredCourseId(id) {
      this.$store.commit("setHoveredCourseId", id);
    },
    resetHoveredCourseId() {
      if (this.hoveredCourseId === "") {
        return;
      }

      this.$store.commit("setHoveredCourseId", "");
    },
    setClickedCourseId(id) {
      this.$store.commit("setClickedCourseId", id);
    },
    resetClickedCourseId() {
      this.$store.commit("setClickedCourseId", "");
    },
    toggleCoursePlayed(id) {
      this.updateCoursePlayed(id);
    },
    toggleFilters() {
      this.$emit("toggle-filters");
    },
    getWrapperHeight() {
      return this.$refs.wrapper.offsetHeight || 0;
    },
    onResize(size) {
      this.report = size;
      // {
      //   width: 20 // width of container (in px)
      //   height: 50 // height of container (in px)
      // }
    },
    handleZoomChanged(newZoom) {
      if (this.zoom === newZoom) {
        return;
      }

      // console.debug(`handleZoomChanged, new: ${newZoom} old: ${this.zoom}`);

      this.zoom = newZoom;
    },
    handleBoundsChanged: debounce(function(newBounds) {
      // console.debug("handleBoundsChanged", newBounds);

      if (!newBounds) {
        return;
      }

      // console.debug("handleBoundsChanged, updating currentMapBounds");

      this.currentMapBounds = newBounds;

      // TODO: should this move to the currentMapBounds watch?
      if (this.smartZoom) {
        this.$nextTick(() => {
          console.debug(
            "handleBoundsChanged, smartZoom is enabled, updating mapBounds",
          );

          this.$store.commit("setMapBounds", newBounds);
        });
      }
    }, 250),
    clickResetFilters() {
      console.debug("clickResetFilters");

      this.$root.$emit("reset-filter-input");
    },
    setLastGeolocation(lastGeolocation) {
      if (!this.isPremium) {
        return;
      }

      console.debug(
        "setLastGeolocation, updating user profile...",
        lastGeolocation,
      );

      const payload = {
        lastGeolocation,
      };

      return this.$store.dispatch("updateUserProfile", payload).then(() => {
        console.debug("updated user profile:", payload);
      });
    },
  },
};
</script>
<style lang="scss">
.gm-style div[role="dialog"].gm-style-iw.gm-style-iw-c {
  padding: 0;

  & > button {
    outline: none;
  }
}

.gm-style iframe + div {
  border: none !important;
}
</style>
<style lang="scss" scoped>
#legend {
  position: absolute;
  bottom: 24px;
  right: 80px;
  padding: 7px;
  font-size: 12px;
  user-select: none;
}

#legend img {
  vertical-align: middle;
}

.filter-status {
  position: absolute;
  top: 12px;
  right: 6px;
  user-select: none;
}

.reset-filters {
  position: absolute;
  top: 78px;
  right: 6px;
  user-select: none;
}

.geolocation {
  position: absolute;
  bottom: 208px;
  right: 10px;
  padding: 4px;

  &--mobile {
    bottom: 24px;
    right: 80px;
  }
}

.action-button {
  position: absolute;
  bottom: 124px;
  left: 10px;

  &--premium {
    bottom: 24px;
  }
}
</style>
